import React, { useState, useEffect } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import ExpandableText from "components/ExpandableText";
import { ApiNames } from 'components/Common/ApiManagement/apiServices'
import 'assets/css/reports/Dashboard.css';

const FrameworkDetail = () => {
  const { frameworkId } = useParams();
  const [searchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState('');
  const [frameworkData, setFrameworkData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTarget, setSelectedTarget] = useState(
    parseInt(searchParams.get('target')) || 5
  );

  // Available targets
  const targets = [
    { id: 5, name: 'www.gsa.gov', label: 'GSA - General Services Administration' },
    { id: 27, name: 'www.whitehouse.gov', label: 'White House' }
  ];


  useEffect(() => {
    const loadFrameworkData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        console.log(`[FrameworkDetail] Loading ${frameworkId} for target ${selectedTarget}`);
        
        // API call for specific framework data
       const response = await fetch(ApiNames.FETCH_FRAMEWORK_DETAIL(selectedTarget, frameworkId));

        
        if (!response.ok) {
          throw new Error(`API Error: ${response.status} ${response.statusText}`);
        }
        
        const result = await response.json();
        
        if (!result.success) {
          throw new Error(result.error || 'Framework data not found');
        }
        
        console.log('[FrameworkDetail] Data loaded successfully:', result.data);
        setFrameworkData(result.data);
        
      } catch (err) {
        console.error('[FrameworkDetail] Error loading data:', err);
        setError(err.message);
        setFrameworkData(null);
      } finally {
        setLoading(false);
      }
    };

    if (frameworkId && selectedTarget) {
      loadFrameworkData();
    }
  }, [frameworkId, selectedTarget]);

  const handleTargetChange = (targetId) => {
    setSelectedTarget(parseInt(targetId));
  };

  const getSelectedTargetName = () => {
    const target = targets.find(t => t.id === selectedTarget);
    return target ? target.label : 'Unknown Target';
  };

  const getStatusIcon = (status) => {
    switch(status?.toLowerCase()) {
      case 'compliant': return '✅';
      case 'non-compliant': return '❌';
      case 'partially compliant': return '⚠️';
      default: return '⏳';
    }
  };

  const getStatusClass = (status) => {
    switch(status?.toLowerCase()) {
      case 'compliant': return 'status-compliant';
      case 'non-compliant': return 'status-non-compliant';
      case 'partially compliant': return 'status-partial';
      default: return 'status-unknown';
    }
  };

  const getPriorityClass = (priority) => {
    switch(priority?.toLowerCase()) {
      case 'critical': return 'priority-critical';
      case 'high': return 'priority-high';
      case 'medium': return 'priority-medium';
      case 'low': return 'priority-low';
      default: return 'priority-unknown';
    }
  };

  const filteredControls = frameworkData?.controls?.filter(control => 
    !searchTerm || 
    control.controlName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    control.controlId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    control.description?.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  if (loading) {
    return (
      <div className="framework-detail-container">
        <div className="detail-header">
          <div className="back-nav">
            <Link to="/" className="back-button">← Back to Dashboard</Link>
          </div>
          <h1>Loading Framework Details...</h1>
        </div>
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading {frameworkId} compliance data for {getSelectedTargetName()}...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="framework-detail-container">
        <div className="detail-header">
          <div className="back-nav">
            <Link to="/" className="back-button">← Back to Dashboard</Link>
          </div>
          <h1>Error Loading Framework</h1>
        </div>
        <div className="error-container">
          <h3>❌ Error</h3>
          <p>{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="retry-button"
          >
            🔄 Retry
          </button>
        </div>
      </div>
    );
  }

  if (!frameworkData) {
    return (
      <div className="framework-detail-container">
        <div className="detail-header">
          <div className="back-nav">
            <Link to="/" className="back-button">← Back to Dashboard</Link>
          </div>
          <h1>No Framework Data Available</h1>
        </div>
      </div>
    );
  }

  return (
    <div className="framework-detail-container">
      {/* Header with Navigation */}
      <div className="detail-header">
        <div className="back-nav">
          <Link to="/" className="back-button">← Back to Dashboard</Link>
        </div>
        
        {/* Target Selector */}
        <div className="target-selector-mini">
          <label>Target: </label>
          <select 
            value={selectedTarget} 
            onChange={(e) => handleTargetChange(e.target.value)}
            className="target-select-mini"
          >
            {targets.map(target => (
              <option key={target.id} value={target.id}>
                {target.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Framework Overview */}
      <div className="framework-overview">
        <div className="framework-title-section">
          <h1 className="framework-title">{frameworkData.framework.name}</h1>
          <p className="framework-description">{frameworkData.framework.description}</p>
          <div className="framework-meta">
            <span className="category-tag">{frameworkData.framework.category}</span>
            <span className="target-tag">{getSelectedTargetName()}</span>
          </div>
        </div>

        <div className="framework-summary-stats">
          <div className="summary-card">
            <div className="summary-label">Pass Rate</div>
            <div className={`summary-value ${frameworkData.framework.passRate >= 80 ? 'good' : frameworkData.framework.passRate >= 60 ? 'warning' : 'poor'}`}>
              {frameworkData.framework.passRate}%
            </div>
          </div>
          
          <div className="summary-card">
            <div className="summary-label">Controls Scanned</div>
            <div className="summary-value">{frameworkData.framework.controlsScanned}</div>
          </div>
          
          <div className="summary-card compliant">
            <div className="summary-label">✅ Compliant</div>
            <div className="summary-value">{frameworkData.framework.summary.compliant}</div>
          </div>
          
          <div className="summary-card non-compliant">
            <div className="summary-label">❌ Non-Compliant</div>
            <div className="summary-value">{frameworkData.framework.summary.nonCompliant}</div>
          </div>
          
          {frameworkData.framework.summary.criticalIssues > 0 && (
            <div className="summary-card critical">
              <div className="summary-label">⚠️ Critical Issues</div>
              <div className="summary-value">{frameworkData.framework.summary.criticalIssues}</div>
            </div>
          )}
        </div>
      </div>

      {/* Controls Search */}
      <div className="controls-section">
        <div className="controls-header">
          <h2>Compliance Controls ({filteredControls.length})</h2>
          <div className="controls-search">
            <input
              type="text"
              placeholder="Search controls by name, ID, or description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            {searchTerm && (
              <button 
                onClick={() => setSearchTerm('')}
                className="clear-search"
              >
                ✕
              </button>
            )}
          </div>
        </div>

        {/* Controls List */}
        <div className="controls-list">
          {filteredControls.length === 0 ? (
            <div className="no-controls">
              {searchTerm ? 
                `No controls found matching "${searchTerm}"` : 
                'No controls data available'
              }
            </div>
          ) : (
            filteredControls.map((control, index) => (
              <div key={control.id || index} className={`control-card ${getStatusClass(control.status)}`}>
                <div className="control-header">
                  <div className="control-id">
                    <span className="control-number">{control.controlId}</span>
                    <span className={`priority-badge ${getPriorityClass(control.priority)}`}>
                      {control.priority}
                    </span>
                  </div>
                  <div className="control-status">
                    <span className={`status-indicator ${getStatusClass(control.status)}`}>
                      {getStatusIcon(control.status)} {control.status}
                    </span>
                    <span className="control-score">{control.score}</span>
                  </div>
                </div>

                <div className="control-content">
                  <h3 className="control-name">{control.controlName}</h3>
                  <p className="control-description">{control.description}</p>
                  
                  <div className="control-details">
                    <div className="detail-section">
                      <h4>🔍 Finding</h4>
                      <p>{control.finding}</p>
                      {control.violations > 0 && (
                        <div className="violations-count">
                          ⚠️ {control.violations} violations detected
                        </div>
                      )}
                    </div>

                    <div className="detail-section">
                      <h4>📋 Evidence</h4>
                      <ExpandableText 
                        text={control.evidence} 
                        maxLength={200}
                        className="evidence-text"
                      />
                    </div>

                    <div className="detail-section">
                      <h4>🔧 Remediation</h4>
                      <ExpandableText 
                        text={control.remediation} 
                        maxLength={200}
                        className="remediation-text"
                      />
                    </div>

                    <div className="control-meta">
                      <div className="meta-item">
                        <span className="meta-label">Scanner:</span>
                        <span className="meta-value">{control.scanner}</span>
                      </div>
                      <div className="meta-item">
                        <span className="meta-label">Timeline:</span>
                        <span className="meta-value">{control.timeline}</span>
                      </div>
                      <div className="meta-item">
                        <span className="meta-label">Category:</span>
                        <span className="meta-value">{control.cwe}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Framework Footer */}
      <div className="framework-footer">
        <div className="footer-actions">
          <Link to="/" className="back-to-dashboard">
            ← Return to Compliance Dashboard
          </Link>
          <button 
            onClick={() => window.location.reload()} 
            className="refresh-data"
          >
            🔄 Refresh Data
          </button>
        </div>
        
        <div className="footer-info">
          <p>
            Compliance data for <strong>{frameworkData.framework.name}</strong> on{' '}
            <strong>{getSelectedTargetName()}</strong>
          </p>
          <p className="last-updated">
            Last updated: {frameworkData.target_info?.processed_at ? 
              new Date(frameworkData.target_info.processed_at).toLocaleDateString() : 
              'September 03, 2025'
            }
          </p>
        </div>
      </div>
    </div>
  );
};

export default FrameworkDetail;